import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/ResponsiveDataTable';
import { getWalletPayments, approveWalletPayment } from '../config/api';
import { toast } from "react-toastify";

const WalletPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    loadWalletPayments();
  }, []);

  const loadWalletPayments = async () => {
    setLoading(true);
    try {
      const data = await getWalletPayments() || {};
      setPayments(data.data || []);
    } catch (error) {
      console.error('Error loading wallet payments:', error);
      toast.error('Failed to load wallet payments');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayment = async (paymentId, payment, action = 'approved') => {
    const actionText = action === 'approved' ? 'approve' : 'reject';
    if (!window.confirm(`Are you sure you want to ${actionText} payment of ₹${payment.amount} for UTR: ${payment.utr}?`)) {
      return;
    }

    try {
      setApprovingId(paymentId);
      const result = await approveWalletPayment(paymentId, action);
      
      if (result.success) {
        // Update local state
        setPayments(payments.map(p => 
          p.id === paymentId 
            ? { 
                ...p, 
                is_approved: action === 'approved', 
                status: action,
                action_taken_by: result.data?.payment?.action_taken_by,
                approved_at: new Date().toISOString() 
              }
            : p
        ));
        toast.success(`Payment ${action} successfully!`);
      } else {
        toast.error(result.message || `Failed to ${actionText} payment`);
      }
    } catch (error) {
      console.error(`Error ${actionText}ing payment:`, error);
      toast.error(`Failed to ${actionText} payment`);
    } finally {
      setApprovingId(null);
    }
  };

  const getStatusBadge = (status) => {
    // Support both old (is_approved boolean) and new (status string) format
    let statusText, bgColor, textColor;
    
    if (typeof status === 'string') {
      statusText = status.charAt(0).toUpperCase() + status.slice(1);
      if (status === 'approved') {
        bgColor = '#198754';
        textColor = 'white';
      } else if (status === 'rejected') {
        bgColor = '#dc3545';
        textColor = 'white';
      } else {
        bgColor = '#ffc107';
        textColor = '#000';
      }
    } else {
      // Backward compatibility with boolean is_approved
      statusText = status ? 'Approved' : 'Pending';
      bgColor = status ? '#198754' : '#ffc107';
      textColor = status ? 'white' : '#000';
    }
    
    return (
      <span 
        className="status-badge" 
        style={{ 
          backgroundColor: bgColor, 
          color: textColor 
        }}
      >
        {statusText}
      </span>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const columns = [
    {
      name: 'Payment ID',
      selector: row => row.id,
      sortable: true,
      width: '140px',
      cell: row => (
        <div style={{ fontWeight: '600', color: '#0d6efd' }}>
          #{row.id}
        </div>
      ),
    },
    {
      name: 'User Details',
      selector: row => row.user_name,
      sortable: true,
      width: '200px',
      cell: row => (
        <div>
          <div style={{ fontWeight: '600', color: '#212529' }}>
            {row.user_name || '-'}
          </div>
          {/* <div style={{ fontSize: '12px', color: '#6c757d' }}>
            ID: {row.user_id}
          </div> */}
        </div>
      ),
    },
    {
      name: 'UTR Number',
      selector: row => row.utr,
      sortable: true,
      width: '190px',
      cell: row => (
        <div style={{ 
          fontFamily: 'monospace', 
          fontSize: '13px', 
          fontWeight: '600',
          color: '#495057'
        }}>
          {row.utr}
        </div>
      ),
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      width: '150px',
      cell: row => (
        <div style={{ 
          fontWeight: '700', 
          fontSize: '16px',
          color: '#198754' 
        }}>
          {formatCurrency(row.amount)}
        </div>
      ),
    },
    {
      name: 'Payment Date',
      selector: row => row.payment_date,
      sortable: true,
      width: '190px',
      cell: row => (
        <div style={{ fontSize: '13px' }}>
          {formatDateTime(row.payment_date)}
        </div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status || (row.is_approved ? 'approved' : 'pending'),
      sortable: true,
      width: '140px',
      cell: row => getStatusBadge(row.status || (row.is_approved ? 'approved' : 'pending')),
    },
    // {
    //   name: 'Submitted On',
    //   selector: row => row.createdAt,
    //   sortable: true,
    //   width: '160px',
    //   cell: row => (
    //     <div style={{ fontSize: '13px' }}>
    //       {formatDateTime(row.createdAt)}
    //     </div>
    //   ),
    // },
    {
      name: 'Actions',
      cell: row => {
        const status = row.status || (row.is_approved ? 'approved' : 'pending');
        
        if (status === 'pending') {
          return (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-success"
                style={{ fontSize: '12px', padding: '6px 12px' }}
                onClick={() => handleApprovePayment(row.id, row, 'approved')}
                disabled={approvingId === row.id}
              >
                {approvingId === row.id ? 'Processing...' : 'Approve'}
              </button>
              <button
                className="btn btn-secondary"
                style={{ 
                  fontSize: '12px', 
                  padding: '6px 12px',
                  backgroundColor: '#dc3545',
                  borderColor: '#dc3545'
                }}
                onClick={() => handleApprovePayment(row.id, row, 'rejected')}
                disabled={approvingId === row.id}
              >
                Reject
              </button>
            </div>
          );
        } else if (status === 'approved') {
          return (
            <span style={{ 
              fontSize: '12px', 
              color: '#198754', 
              fontWeight: '500' 
            }}>
              ✓ Approved
            </span>
          );
        } else if (status === 'rejected') {
          return (
            <span style={{ 
              fontSize: '12px', 
              color: '#dc3545', 
              fontWeight: '500' 
            }}>
              ✗ Rejected
            </span>
          );
        }
      },
      ignoreRowClick: true,
      width: '180px',
    },
  ];

  // Filter data based on search text
  const filteredItems = payments.filter(
    item => {
      const searchText = filterText.toLowerCase();
      const status = item.status || (item.is_approved ? 'approved' : 'pending');
      return (
        item.utr.toLowerCase().includes(searchText) ||
        (item.user_name && item.user_name.toLowerCase().includes(searchText)) ||
        item.user_id.toString().includes(searchText) ||
        item.amount.toString().includes(searchText) ||
        status.includes(searchText)
      );
    }
  );

  const subHeaderComponent = useMemo(() => {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        width: '100%',
        padding: '8px 0'
      }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
          <input
            type="text"
            placeholder="Search payments..."
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            style={{
              padding: '10px 14px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              width: '100%',
              transition: 'all 0.3s ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#0d6efd';
              e.target.style.boxShadow = '0 0 0 2px rgba(13, 110, 253, 0.25)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ced4da';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ 
            padding: '8px 16px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#856404'
          }}>
            <strong>Total Pending:</strong> {payments.filter(p => {
              const status = p.status || (p.is_approved ? 'approved' : 'pending');
              return status === 'pending';
            }).length}
          </div>
          <div style={{ 
            padding: '8px 16px', 
            backgroundColor: '#d1e7dd', 
            border: '1px solid #badbcc',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#0f5132'
          }}>
            <strong>Total Approved:</strong> {payments.filter(p => {
              const status = p.status || (p.is_approved ? 'approved' : 'pending');
              return status === 'approved';
            }).length}
          </div>
          <div style={{ 
            padding: '8px 16px', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c2c7',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#842029'
          }}>
            <strong>Total Rejected:</strong> {payments.filter(p => {
              const status = p.status || (p.is_approved ? 'approved' : 'pending');
              return status === 'rejected';
            }).length}
          </div>
        </div>
      </div>
    );
  }, [filterText, payments]);

  const customStyles = {
    header: {
      style: {
        minHeight: '56px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#dee2e6',
        backgroundColor: '#f8f9fa',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        backgroundColor: '#f8f9fa',
        fontWeight: '600',
        fontSize: '14px',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '16px',
      },
    },
    cells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        padding: '16px',
        fontSize: '14px',
      },
    },
    rows: {
      style: {
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
        borderBottomColor: '#dee2e6',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #dee2e6',
        padding: '16px',
      },
    },
  };

  return (
    <div className="container">
      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 className="page-title">Wallet Payments</h1>
            <p className="page-subtitle">Manage offline wallet payment approvals</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ 
              padding: '8px 16px', 
              backgroundColor: '#e7f3ff', 
              border: '1px solid #b3d9ff',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#0056b3'
            }}>
              <strong>💰 Finance Access Required</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table Card */}
      <div className="card">
        <DataTable
          columns={columns}
          data={filteredItems}
          progressPending={loading}
          pagination
          paginationResetDefaultPage={false}
          subHeader
          subHeaderComponent={subHeaderComponent}
          selectableRows={false}
          customStyles={customStyles}
          highlightOnHover
          responsive
          striped
          paginationPerPage={15}
          paginationRowsPerPageOptions={[10, 15, 25, 50]}
          noDataComponent={
            <div style={{ 
              padding: '60px 40px', 
              textAlign: 'center',
              color: '#6c757d',
              fontSize: '16px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>No wallet payments found</div>
              <div>Wallet payment requests will appear here when submitted</div>
            </div>
          }
          defaultSortFieldId="created_at"
          defaultSortAsc={false}
        />
      </div>
    </div>
  );
};

export default WalletPayments;